import { memo, type PropsWithChildren } from 'react';
import { useSelector } from 'react-redux';

import { UploadDropzone } from '~/components/PrivateDropzone/components/UploadDropzone';
import { DirectUploadZoneProvider } from '~/components/PrivateDropzone/DirectUploadZoneContext';
import { useShowFirstUploadPromptValue } from '~/components/v2/store/features/onboarding/useOnboardingPreferencesStore';
import { useUploadPrivateFiles } from '~/hooks/useUploadPrivateFiles';
import { useCurrentWorkspace } from '~/providers/CurrentWorkspaceProvider';
import { PrivateUploadUrlsManagerProvider } from '~/providers/PrivateUploadUrlsManagerProvider';
import { centralizedClipIdSelector } from '~/store/centralizedClip/selectors';

export type PrivateUploadProviderProps = PropsWithChildren<{
  currentBoardTitle: string | undefined;
  /**
   * Block page drag-and-drop while leaving the file picker available.
   * Used for view-only boards and picker-only hosts that must not become
   * a drop target.
   */
  disableDrag?: boolean;
  disabled: boolean;
  supportsDirectUpload: boolean;
  supportsSimilarToImageSearch: boolean;
}>;

export const PrivateUploadProvider = memo(
  ({
    children,
    currentBoardTitle,
    disableDrag = false,
    disabled,
    supportsDirectUpload,
    supportsSimilarToImageSearch,
  }: PrivateUploadProviderProps) => {
    const { currentWorkspace } = useCurrentWorkspace();
    const { uploadPrivateFiles } = useUploadPrivateFiles();
    const clipId = useSelector(centralizedClipIdSelector);
    const showFirstUploadPrompt = useShowFirstUploadPromptValue(currentWorkspace?.id);

    return (
      <PrivateUploadUrlsManagerProvider>
        {/* UploadDropzone always reads DirectUploadZoneContext; a picker-only host never claims a zone. */}
        <DirectUploadZoneProvider>
          <UploadDropzone
            currentBoardTitle={currentBoardTitle}
            currentWorkspaceName={currentWorkspace?.name}
            // An open asset still uses PrivateAssetModalDropzone for versions.
            // Disabling the page dropzone entirely also disables workspace Uploads
            // (`openUploader` / file input) while the rail pane is meant to pick a board.
            disableDrag={disableDrag || !!clipId}
            disabled={disabled || showFirstUploadPrompt}
            onUpload={uploadPrivateFiles}
            supportsDirectUpload={supportsDirectUpload}
            supportsSimilarToImageSearch={supportsSimilarToImageSearch}
          >
            {children}
          </UploadDropzone>
        </DirectUploadZoneProvider>
      </PrivateUploadUrlsManagerProvider>
    );
  },
);

PrivateUploadProvider.displayName = 'PrivateUploadProvider';
